import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Boxes } from "lucide-react";

const NAV = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/alfie", label: "Alfie AI" },
  { to: "/website-addon", label: "Website Add-on" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Brand({ dark, className = "" }) {
  return (
    <Link to="/" className={`flex items-center gap-2 font-display font-extrabold text-xl ${className}`} data-testid="brand-logo">
      <span className="h-9 w-9 rounded-full overflow-hidden"><img src="/amden-logo.png?v=2" alt="Amden One" className="h-full w-full object-cover" /></span>
      <span className={dark ? "text-white" : "text-navy"}>Amden<span className="text-blue-accent">One</span></span>
    </Link>
  );
}

export default function MarketingLayout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Brand />
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "text-blue-accent bg-blue-50" : "text-slate-600 hover:text-navy hover:bg-slate-50"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            <Link to="/login" data-testid="header-signin" className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors">Sign in</Link>
            <Link to="/register" data-testid="header-getstarted" className="px-4 py-2 rounded-lg text-sm font-semibold bg-navy text-white hover:bg-slate-800 shadow-sm transition-colors">Start free</Link>
          </div>
          <button className="lg:hidden p-2 text-navy" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
            {NAV.map((n) => <Link key={n.to} to={n.to} className="block px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-50">{n.label}</Link>)}
            <div className="flex gap-2 pt-2">
              <Link to="/login" className="flex-1 text-center px-4 py-2 rounded-lg border border-slate-300">Sign in</Link>
              <Link to="/register" className="flex-1 text-center px-4 py-2 rounded-lg bg-navy text-white">Start free</Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="bg-navy-900 text-slate-300 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <Brand dark />
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">Run your business in one place. CRM, quotes, invoices, jobs and more for small businesses and tradespeople.</p>
          </div>
          <FooterCol title="Product" links={[["Features", "/features"], ["Pricing", "/pricing"], ["Alfie AI", "/alfie"], ["Website Add-on", "/website-addon"]]} />
          <FooterCol title="Company" links={[["About Amden", "/about"], ["Contact", "/contact"], ["Sign in", "/login"], ["Create account", "/register"]]} />
          <FooterCol title="Legal" links={[["Privacy Policy", "/privacy"], ["Terms & Conditions", "/terms"]]} />
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">© {new Date().getFullYear()} Amden One. All rights reserved.</div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="text-white font-semibold text-sm mb-4">{title}</h4>
      <ul className="space-y-2 text-sm">
        {links.map(([l, to]) => <li key={to}><Link to={to} className="text-slate-400 hover:text-white transition-colors">{l}</Link></li>)}
      </ul>
    </div>
  );
}
